import React, {
    PropsWithChildren,
    SetStateAction,
    useCallback,
    useEffect,
    useMemo,
    useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import NotificationManager from '../../../common/services/notification-manager';
import { useToast } from '../toast/toast.context';
import { INotificationsResponse } from '../../../common/components/custom-header/components/notifications/Interfaces/INotificationsResponse';
import AuthContext from '../auth';

interface NotificationsContextProps {
    newNotifications?: boolean;
    updateNotificationsCounter: () => void;
    getAllNotificationsPaginated: (
        setNotifications: (
            value: SetStateAction<INotificationsResponse[]>,
        ) => void,
    ) => void;
    markAllNotificationsAsRead: () => void;
    markNotificationAsRead: (notificationId: string) => void;
}

export const NotificationsContext =
    React.createContext<NotificationsContextProps>(
        {} as NotificationsContextProps,
    );

export function NotificationsProvider({
    children,
}: Readonly<PropsWithChildren>) {
    const { t } = useTranslation();
    const { setToast } = useToast();
    const { user, isThirdUser } = AuthContext.useAuth();

    const [totalNotifications, setTotalNotifications] = useState(0);
    const [newNotifications, setNewNotifications] = useState<
        boolean | undefined
    >(undefined);

    const loggedUser = useMemo(() => user === null, [user]);

    const getAllNotificationsPaginated = useCallback(
        (
            setNotifications: (
                value: SetStateAction<INotificationsResponse[]>,
            ) => void,
        ) => {
            NotificationManager.getAllNotificationsPaginated(
                isThirdUser(),
                totalNotifications,
            ).then(({ response, error }) => {
                if (error !== undefined) {
                    setNotifications([] as INotificationsResponse[]);
                    setToast('negative', t('Erro'), error.message);
                } else {
                    setNotifications(response!.data);
                }
            });
        },
        [isThirdUser, totalNotifications, setToast, t],
    );

    const markAllNotificationsAsRead = useCallback(() => {
        NotificationManager.markAllAsRead(isThirdUser()).then(({ error }) => {
            if (error !== undefined) {
                setToast('negative', t('Erro'), error.message);
            }
        });
    }, [isThirdUser, setToast, t]);

    const markNotificationAsRead = useCallback(
        (notificationId: string) => {
            NotificationManager.markAsRead(notificationId).then(({ error }) => {
                if (error !== undefined) {
                    setToast('negative', t('Erro'), error.message);
                }
            });
        },
        [setToast, t],
    );

    const updateNotificationsCounter = useCallback(() => {
        NotificationManager.getNotificationsCounter(isThirdUser()).then(
            ({ response, error }) => {
                if (error !== undefined) {
                    setToast('negative', t('Erro'), error.message);
                    return;
                }

                setNewNotifications(response!.unread > 0);
                setTotalNotifications(response!.totalItems);
            },
        );
    }, [isThirdUser, setToast, t]);

    const startFetchNotificationsCounter = useCallback(():
        | NodeJS.Timeout
        | undefined => {
        if (!loggedUser && newNotifications === undefined) {
            updateNotificationsCounter();
        }

        let intervalId: NodeJS.Timeout | undefined;

        if (!loggedUser) {
            intervalId = setInterval(updateNotificationsCounter, 90 * 1000);
        }

        return intervalId;
    }, [loggedUser, newNotifications, updateNotificationsCounter]);

    useEffect(() => {
        const intervalId = startFetchNotificationsCounter();

        return () => {
            if (intervalId !== undefined) clearInterval(intervalId);
        };
    }, [
        loggedUser,
        newNotifications,
        updateNotificationsCounter,
        startFetchNotificationsCounter,
    ]);

    const notificationsProvider = useMemo(
        (): NotificationsContextProps => ({
            newNotifications,
            updateNotificationsCounter,
            getAllNotificationsPaginated,
            markAllNotificationsAsRead,
            markNotificationAsRead,
        }),
        [
            newNotifications,
            updateNotificationsCounter,
            getAllNotificationsPaginated,
            markNotificationAsRead,
            markAllNotificationsAsRead,
        ],
    );

    return (
        <NotificationsContext.Provider value={notificationsProvider}>
            {children}
        </NotificationsContext.Provider>
    );
}

export const useNotifications = () => React.useContext(NotificationsContext);
